import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, Building2, CalendarDays, Network, AlertTriangle, ShieldAlert, Sparkles, ArrowRight, Wand2, CalendarCheck, Bot, Scale } from "lucide-react";
import KPISnapshotCard from "@/components/KPISnapshotCard";
import AutomationPreviewCard from "@/components/AutomationPreviewCard";
import ReadinessWidget from "@/components/ReadinessWidget";
import PlanGuidanceTile from "@/components/PlanGuidanceTile";
import { hasCommandAccess, hasGrowthAccess } from "@/lib/effectivePlan";
import { RosterDraftNudge } from "@/pages/RosterPage";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [company, setCompany] = useState(null);
  // Window (Mon..Sun ISO) of the soonest awaiting-review draft rota.
  // Reused by <RosterDraftNudge /> so the Overview shows the same nudge
  // the Roster page already shows for THAT week (which may be the
  // future week the RSA scheduler has just drafted).
  const [pendingWeekDays, setPendingWeekDays] = useState(null);

  useEffect(() => {
    (async () => {
      if (user.role === "super_admin") {
        const { data } = await api.get("/stats/platform");
        setStats(data);
        const c = await api.get("/companies");
        setEmployees(c.data); // reuse var
      } else {
        const { data } = await api.get("/stats/company");
        setStats(data);
        if (user.role !== "employee") {
          const t = await api.get("/teams"); setTeams(t.data);
          const e = await api.get("/employees"); setEmployees(e.data);
        }
        api.get("/companies").then(r => setCompany((r.data || [])[0] || null)).catch(()=>{});
        if (user.employee_id) {
          api.get("/me/leave-balance").then(r => setLeaveBalance(r.data)).catch(()=>{});
        }
        // Find the soonest awaiting-review draft rota (RSA seeds for
        // future weeks, so we cannot rely on the current calendar
        // week). The list endpoint sorts window_start desc; pick the
        // earliest awaiting row instead.
        // TP-AT.RS.8.8 (Feb 2026) -- gated on `user.role ===
        // "super_admin"` because normal Workspace / Team Admin
        // managers should not be pushed into the legacy Draft rotas
        // workflow. Modern automation writes drafts directly into
        // the Weekly roster and auto-publishes from there. The
        // legacy pending-review nudge is retained only for internal
        // / support usage via super_admin.
        if (user.role === "super_admin") {
          api.get("/drafts/rota?status=awaiting_review").then(r => {
            const rows = (r.data?.periods || []).filter(
              p => p.status === "awaiting_review");
            if (!rows.length) { setPendingWeekDays(null); return; }
            rows.sort((a, b) => (a.window_start || "").localeCompare(b.window_start || ""));
            setPendingWeekDays(weekDaysFromWindow(rows[0].window_start));
          }).catch(() => setPendingWeekDays(null));
        }
      }
    })();
  }, [user.role, user.company_id, user.employee_id]);

  if (user.role === "super_admin") {
    return (
      <div>
        <div className="eyebrow">Platform</div>
        <h1 className="font-display text-4xl mt-1 mb-8">Good day, {user.name.split(" ")[0]}.</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Companies" value={stats?.companies ?? "-"} icon={<Building2 className="h-4 w-4"/>} />
          <Stat label="Active companies" value={stats?.active_companies ?? "-"} icon={<Building2 className="h-4 w-4"/>} />
          <Stat label="Users" value={stats?.users ?? "-"} icon={<Users className="h-4 w-4"/>} />
          <Stat label="Employees" value={stats?.employees ?? "-"} icon={<Users className="h-4 w-4"/>} />
        </div>
        <Card title="All companies">
          <div className="divide-y divide-slate-100">
            {employees.map((c) => (
              <div key={c.company_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-[#4B5773]">{c.country} · {c.timezone}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-500"}`}>
                  {c.is_active ? "Active" : "Disabled"}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="overview-command-centre">
      <div className="eyebrow tracking-[0.18em] text-[#5775A6]" data-testid="overview-eyebrow">
        {user.role === "company_admin" ? "Company overview" : user.role === "team_admin" ? "Team overview" : "Your week"}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-1.5 mb-1 text-[#0B1A36]">
        Hello, {user.name.split(" ")[0]}.
      </h1>
      <TrialSubline company={company} role={user.role} />

      {/* TP-RS.team.2 Phase 3 hotfix #6 -- employee Overview polish.
          Trial / commercial / admin-style tiles (PlanGuidanceTile,
          stats grid, TodayPanels, ExpiringCertsCard, KPISnapshotCard,
          People list, Teams card) are hidden for the employee role
          and replaced with an employee-focused panel that surfaces
          leave entitlement + remaining + next-leave + upcoming bank
          holidays. Permission-agnostic - any future read-only WA or
          read-only TA will inherit the existing admin view, not the
          employee view, unless we explicitly opt them into it.
          TP-Billing-RBAC (Feb 2026) -- plan / billing / trial / "Pick
          a plan" messaging is commercial-owner content. Hide for
          Team Admin and Employee so only Workspace Admin (and a
          super_admin impersonating them) sees the upgrade nudge.
          Plan gating itself is unchanged; this is visibility only. */}
      {user.role === "company_admin" && <PlanGuidanceTile />}

      {user.role === "company_admin" && (
        <ContinueSetupBanner companyId={user.company_id} />
      )}

      {/* R3 (Feb 2026) - Rota Style setup notification card. Shown to
          Workspace Admins on the Overview once R1
          `account_setup_complete` is true. Copy branches by plan
          (Starter locked message + open Roster; Growth = open Rota
          Style; Command = both). Uses existing plan helpers and
          existing endpoints - no new backend state, no new
          endpoint, no new dashboard framework. */}
      {user.role === "company_admin" && (
        <RotaStyleSetupOverviewCard />
      )}

      {/* Phase 2Z.m Phase 1 — permission-aware quick actions */}
      <QuickActions role={user.role} />

      {/* Phase 2Z.m Phase 2 — Rota Mix Intelligence banner + */}
      {/* Automation status (admin/manager only). */}
      <Phase2OverviewCards role={user.role} />

      {/* Phase 2Z.m Phase 3 — Fairness & Balance advisory (read-only). */}
      {/* One soft insight, derived from existing /reports/company data. */}
      <FairnessBalanceCard role={user.role} />

      {(user.role === "company_admin" || user.role === "team_admin") && pendingWeekDays && (
        <RosterDraftNudge weekDays={pendingWeekDays} />
      )}

      {leaveBalance && (
        <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="leave-balance-strip">
          <Stat label="Annual leave entitlement" value={`${leaveBalance.entitlement_days}d`} />
          <Stat label="Rollover from last year" value={`${leaveBalance.rollover_days}d`} />
          <Stat label="Leave taken" value={`${leaveBalance.used_days}d`} />
          <Stat label="Remaining" value={`${leaveBalance.remaining_days}d`} />
          {leaveBalance.warning && (
            <div className="col-span-2 sm:col-span-4 -mt-1 flex items-start gap-2 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0"/>
              {leaveBalance.warning}
            </div>
          )}
        </div>
      )}

      {user.role === "employee" && (
        <EmployeeNextLeaveCard />
      )}

      {(user.role === "company_admin" || user.role === "team_admin" || user.role === "super_admin") && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
          <Stat label="Active employees" value={stats?.employees ?? "-"} icon={<Users className="h-4 w-4"/>}
            emptyCta={user.role === "company_admin" || user.role === "team_admin" ? { to: "/app/employees", label: "Add your first employee" } : null}
            testId="stat-employees"/>
          <Stat label="Teams" value={stats?.teams ?? "-"} icon={<Network className="h-4 w-4"/>}
            emptyCta={user.role === "company_admin" ? { to: "/app/teams", label: "Create a team" } : null}
            testId="stat-teams"/>
          <Stat label="Shifts this week" value={stats?.shifts_this_week ?? "-"} icon={<CalendarDays className="h-4 w-4"/>}
            emptyCta={user.role === "company_admin" || user.role === "team_admin" ? { to: "/app/roster", label: "Build this week's roster" } : null}
            testId="stat-shifts"/>
        </div>
      )}

      {(user.role === "company_admin" || user.role === "team_admin") && (
        <TodayPanels />
      )}

      {(user.role === "company_admin" || user.role === "team_admin") && (
        <div className="mb-10">
          <ExpiringCertsCard />
        </div>
      )}

      {(user.role === "company_admin" || user.role === "team_admin") && (
        <div className="mb-10">
          <KPISnapshotCard />
        </div>
      )}

      {/* V1: ReadinessWidget + AutomationPreviewCard hidden for normal
          customers — they reference the old gap-engine readiness gates
          and are confusing now that Rota Style Automation is the
          single V1 path. Super-admins can still see them for
          diagnostics. */}
      {user.role === "super_admin" && (
        <div className="mb-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <AutomationPreviewCard />
          </div>
          <ReadinessWidget />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {user.role !== "employee" && (
          <div className="lg:col-span-2">
            <Card title="People" action={user.role !== "employee" ? <Link to="/app/employees" className="text-sm text-[#2563EB] hover:underline">Manage →</Link> : null}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {employees.slice(0, 6).map((e) => (
                  <li key={e.employee_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <Avatar name={e.name} url={e.avatar_url} />
                    <div>
                      <div className="text-sm font-medium leading-tight">{e.name}</div>
                      <div className="text-xs text-[#4B5773]">{e.country} · {e.timezone}</div>
                    </div>
                  </li>
                ))}
                {!employees.length && <li className="text-sm text-[#4B5773]">No employees yet.</li>}
              </ul>
            </Card>
          </div>
        )}
        {user.role !== "employee" && (
          <Card title="Teams" action={user.role === "company_admin" ? <Link to="/app/teams" className="text-sm text-[#2563EB] hover:underline">Manage →</Link> : null}>
            <ul className="space-y-2">
              {teams.filter(t=>!t.parent_team_id).map((t) => (
                <li key={t.team_id}>
                  <div className="text-sm font-medium">{t.name}</div>
                  <ul className="ml-3 mt-1 space-y-1">
                    {teams.filter(s => s.parent_team_id === t.team_id).map((s) => (
                      <li key={s.team_id} className="text-xs text-[#4B5773]">↳ {s.name}</li>
                    ))}
                  </ul>
                </li>
              ))}
              {!teams.length && <li className="text-sm text-[#4B5773]">No teams yet.</li>}
            </ul>
          </Card>
        )}
      </div>
      <div className="mt-6">
        <Card>
          <Link to={user.role === "employee" ? "/app/my-roster" : "/app/roster"} data-testid="open-roster-cta"
            className="flex items-center justify-between p-2 -m-2 rounded-lg hover:bg-slate-50">
            <div>
              <div className="font-display text-xl">{user.role === "employee" ? "Open my roster" : "Open this week's roster"}</div>
              <div className="text-sm text-[#4B5773] mt-1">
                {user.role === "employee"
                  ? "See your published shifts, breaks and assignment timeline."
                  : "Plan shifts across employees, days, breaks and totals."}
              </div>
            </div>
            <span className="text-[#2563EB] text-sm font-medium">Go →</span>
          </Link>
        </Card>
      </div>
    </div>
  );
}

export function Avatar({ name, url, size = 36 }) {
  const initials = (name || "?").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();
  return url ? (
    <img src={url} alt={name} style={{ width: size, height: size }}
      className="rounded-full object-cover border border-slate-200" />
  ) : (
    <div style={{ width: size, height: size }}
      className="rounded-full bg-slate-900 text-white grid place-items-center text-xs font-medium">{initials}</div>
  );
}

// TP-RS.team.2 Phase 3 hotfix #6 -- employee-focused Overview panel.
// Shows two small cards: next approved leave and upcoming bank
// holidays in the next 30 days. Permission-agnostic - only rendered
// from the parent's employee branch. Failures degrade silently so a
// broken endpoint never crashes the Overview.
function EmployeeNextLeaveCard() {
  const [nextLeave, setNextLeave] = useState(null);
  const [holidays, setHolidays] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leave-requests?status=approved");
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = (data || [])
          .filter((l) => (l.start_date || "") >= today)
          .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        setNextLeave(upcoming[0] || null);
      } catch { /* swallow, panel just hides */ }
      try {
        const { data } = await api.get("/bank-holidays");
        const today = new Date().toISOString().slice(0, 10);
        const in30 = new Date(); in30.setDate(in30.getDate() + 30);
        const horizon = in30.toISOString().slice(0, 10);
        const list = (data || [])
          .filter((h) => (h.date || "") >= today && (h.date || "") <= horizon)
          .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
        setHolidays(list);
      } catch { /* swallow */ }
    })();
  }, []);

  const daysUntil = nextLeave && nextLeave.start_date
    ? Math.max(0, Math.round(
        (new Date(nextLeave.start_date) - new Date()) / 86400000))
    : null;

  return (
    <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4"
         data-testid="employee-overview-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-5"
           data-testid="employee-next-leave-card">
        <div className="flex items-center justify-between mb-3 text-[#4B5773]">
          <span className="eyebrow">Next approved leave</span>
          <CalendarCheck className="h-4 w-4" />
        </div>
        {nextLeave ? (
          <>
            <div className="font-display text-3xl"
                 data-testid="employee-next-leave-days">
              {daysUntil === 0 ? "Today" : `${daysUntil}d`}
            </div>
            <div className="text-xs text-[#4B5773] mt-1">
              {`Starts ${nextLeave.start_date} - ${nextLeave.leave_type || "leave"}`}
            </div>
          </>
        ) : (
          <>
            <div className="font-display text-3xl text-[#4B5773]">-</div>
            <div className="text-xs text-[#4B5773] mt-1">
              No upcoming approved leave.{" "}
              <Link to="/app/leave" className="text-[#2563EB] hover:underline">
                Request leave
              </Link>
            </div>
          </>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5"
           data-testid="employee-bank-holidays-card">
        <div className="flex items-center justify-between mb-3 text-[#4B5773]">
          <span className="eyebrow">Bank holidays in the next 30 days</span>
          <CalendarDays className="h-4 w-4" />
        </div>
        {holidays.length === 0 ? (
          <div className="text-xs text-[#4B5773]">
            No bank holidays in the next 30 days.
          </div>
        ) : (
          <ul className="space-y-1" data-testid="employee-bank-holidays-list">
            {holidays.slice(0, 5).map((h) => (
              <li key={`${h.country_code}-${h.date}`}
                  className="text-sm text-[#0B1A36] flex justify-between">
                <span>{h.name}</span>
                <span className="text-xs text-[#4B5773]">{h.date}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function Stat({ label, value, icon, emptyCta, testId }) {
  const isZero = value === 0 || value === "0";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 text-[#4B5773]">
        <span className="eyebrow">{label}</span>{icon}
      </div>
      <div className="font-display text-3xl">{value}</div>
      {isZero && emptyCta && (
        <Link to={emptyCta.to} data-testid={`${testId}-cta`}
          className="mt-2 inline-flex items-center text-xs font-medium text-[#2563EB] hover:underline">
          {emptyCta.label} →
        </Link>
      )}
    </div>
  );
}

function TrialSubline({ company, role }) {
  if (role !== "company_admin" || !company) return null;
  if (company.status !== "trial" || !company.trial_end_date) return null;
  // Friendly UK format: "9 December 2026"
  let formatted = company.trial_end_date;
  try {
    const d = new Date(company.trial_end_date);
    if (!Number.isNaN(d.getTime())) {
      formatted = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    }
  } catch { /* keep ISO fallback */ }
  return (
    <p className="text-sm text-[#4B5773] mt-1" data-testid="trial-subline">
      Your 3-month trial ends on <span className="font-medium text-[#1F2A44]">{formatted}</span>.
    </p>
  );
}

export function Card({ title, action, children, ...rest }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" {...rest}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="font-display text-lg">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/** Today / Skills / Leave summary cards for Company Admin + Team Admin. */
function TodayPanels() {
  const [today, setToday] = useState(null);
  const [skills, setSkills] = useState(null);
  const [leave, setLeave] = useState(null);

  useEffect(() => {
    api.get("/dashboard/today").then(r => setToday(r.data)).catch(()=>{});
    api.get("/dashboard/skills-overview").then(r => setSkills(r.data)).catch(()=>{});
    api.get("/dashboard/leave-overview").then(r => setLeave(r.data)).catch(()=>{});
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
      {/* Today */}
      <Card title="Today" data-testid="dash-today-card"
            action={today && <span className="text-xs text-[#4B5773]">{today.date}</span>}>
        {!today ? <Skel /> : (
          <ul className="space-y-2.5 text-sm">
            <Row testId="today-scheduled" label="Scheduled" value={today.totals.scheduled} tone="text-[#0B1A36]" />
            <Row testId="today-available" label="Available, no shift" value={today.totals.available} tone="text-[#16A34A]" />
            <Row testId="today-leave"     label="On approved leave" value={today.totals.on_leave} tone="text-[#7C3AED]" />
            <Row testId="today-bh"        label="On bank holiday" value={today.totals.on_bank_holiday} tone="text-[#0EA5E9]" />
            {today.on_leave?.length > 0 && (
              <li className="pt-2 border-t border-slate-100">
                <div className="eyebrow text-[10px] mb-1">Out today</div>
                <ul className="text-xs text-[#4B5773] space-y-0.5">
                  {today.on_leave.slice(0,4).map((p,i) => (
                    <li key={i}>{p.name} · {p.category_name}</li>
                  ))}
                </ul>
              </li>
            )}
          </ul>
        )}
      </Card>

      {/* Skills this week */}
      <Card title="Skills this week" data-testid="dash-skills-card"
            action={skills && <Link to="/app/skills" className="text-xs text-[#2563EB] hover:underline">Manage →</Link>}>
        {!skills ? <Skel /> : skills.skill_count === 0 ? (
          <div className="text-sm text-[#4B5773]">
            No skills defined yet. <Link to="/app/skills" className="text-[#2563EB] hover:underline">Add your first skill</Link>.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-[#DBE7FE] text-[#1D4ED8]" data-testid="skills-covered">
                {skills.covered} covered
              </span>
              {skills.missing > 0 && (
                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800" data-testid="skills-missing">
                  {skills.missing} missing
                </span>
              )}
            </div>
            <ul className="space-y-1.5 text-xs max-h-44 overflow-y-auto pr-1">
              {skills.skills.slice(0, 8).map(s => (
                <li key={s.skill_id} className="flex justify-between gap-2"
                    data-testid={`skill-coverage-${s.skill_id}`}>
                  <span className="truncate">{s.name}</span>
                  <span className={s.covered ? "text-[#16A34A]" : "text-amber-700"}>
                    {s.covered ? `${s.scheduled_this_week.length} on shift` : "no cover"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Upcoming leave / holidays */}
      <Card title="Next 30 days" data-testid="dash-leave-card">
        {!leave ? <Skel /> : (
          <div className="space-y-3 text-sm">
            <div>
              <div className="eyebrow text-[10px] mb-1">Approved leave</div>
              {leave.upcoming_leave?.length ? (
                <ul className="space-y-1 text-xs">
                  {leave.upcoming_leave.slice(0,4).map((l,i) => (
                    <li key={i} data-testid={`upcoming-leave-${i}`}>
                      <span className="text-[#0B1A36] font-medium">{l.name}</span>{" "}
                      <span className="text-[#4B5773]">· {l.start_date}{l.end_date !== l.start_date ? ` → ${l.end_date}` : ""}</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-[#4B5773]">None coming up.</div>}
            </div>
            <div>
              <div className="eyebrow text-[10px] mb-1">Bank holidays</div>
              {leave.holidays_country_setup_needed ? (
                <div
                  className="text-xs text-[#4B5773] bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                  data-testid="dash-bh-setup-needed"
                >
                  Add your company country in setup to show relevant
                  bank holidays.
                </div>
              ) : leave.upcoming_holidays?.length ? (
                <ul className="space-y-1 text-xs">
                  {leave.upcoming_holidays.slice(0,4).map((b,i) => (
                    <li key={i} data-testid={`upcoming-bh-${i}`}>
                      <span className="text-[#0B1A36] font-medium">{b.date}</span>{" "}
                      <span className="text-[#4B5773]">· {b.name} ({b.country_code})</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-[#4B5773]">None coming up.</div>}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ label, value, tone, testId }) {
  return (
    <li className="flex justify-between items-baseline" data-testid={testId}>
      <span className="text-[#4B5773]">{label}</span>
      <span className={`font-display text-2xl ${tone}`}>{value}</span>
    </li>
  );
}
function Skel() { return <div className="h-24 rounded-lg bg-slate-50 animate-pulse"/>; }

/**
 *  read-only widget. Surfaces employee certifications that are
 * already expired or expire within 30 days. Sourced entirely from the
 * existing employee_skills collection, no automation, no blocking logic.
 * The card hides itself when there is nothing to show so it does not add
 * noise to a clean dashboard.
 */
function ExpiringCertsCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/expiring-certs")
      .then(r => setData(r.data))
      .catch(() => setData({ expired: [], expiring: [], total: 0 }));
  }, []);
  if (!data) return null;
  if (!data.total) return null;
  const items = [...data.expired, ...data.expiring];
  return (
    <Card data-testid="dash-expiring-certs-card"
      title={<span className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-600"/>Certifications expiring
      </span>}
      action={<Link to="/app/employees" className="text-xs text-[#2563EB] hover:underline">Manage employees →</Link>}
    >
      <div className="flex flex-wrap gap-2 text-xs mb-3">
        {data.expired.length > 0 && (
          <span data-testid="expiring-certs-expired-count"
                className="px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
            {data.expired.length} expired
          </span>
        )}
        {data.expiring.length > 0 && (
          <span data-testid="expiring-certs-soon-count"
                className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {data.expiring.length} expiring in ≤30 days
          </span>
        )}
      </div>
      <ul className="divide-y divide-slate-100 -mx-2">
        {items.slice(0, 8).map((it, i) => {
          const expired = it.days_until_expiry < 0;
          return (
            <li key={`${it.employee_id}-${it.skill_id}`}
                data-testid={`expiring-cert-row-${i}`}
                className="px-2 py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#0B1A36] truncate">
                  {it.employee_name}
                </div>
                <div className="text-xs text-[#4B5773] truncate">
                  {it.skill_name}
                  {it.cert_reference && <span className="ml-1">· {it.cert_reference}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${
                  expired
                    ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {expired
                    ? `Expired ${Math.abs(it.days_until_expiry)}d ago`
                    : `In ${it.days_until_expiry}d`}
                </span>
                <div className="text-[11px] text-[#4B5773] mt-0.5">{it.cert_expiry_date}</div>
              </div>
            </li>
          );
        })}
      </ul>
      {items.length > 8 && (
        <div className="mt-2 text-xs text-[#4B5773]" data-testid="expiring-certs-more">
          + {items.length - 8} more
        </div>
      )}
    </Card>
  );
}


// Build Mon..Sun ISO dates from a window_start (already a Monday in
// `draft_rota_periods`). RosterDraftNudge then hits /drafts/by-day/{d}
// for each day so the Overview reuses the exact same counts and link
// behaviour as the Roster page.
function weekDaysFromWindow(windowStart) {
  if (!windowStart) return null;
  const start = new Date(windowStart + "T00:00:00Z");
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}



// Phase 2Z.k Option C — Continue Setup banner. Replaces the old
// in-place "Get your workspace ready" checklist for company admins.
// Behaviour:
//   1. On first overview visit per session, if setup is incomplete,
//      gently redirect to /app/setup so the premium Guided Setup
//      Assistant becomes the primary onboarding surface.
//   2. On subsequent visits the banner stays as a small "Continue
//      setup" card pointing at /app/setup. Never blocks Overview.
//   3. When setup is 100% complete the banner unmounts itself.
function ContinueSetupBanner() {
  const [data, setData] = useState(null);
  const [shiftTemplatesCount, setShiftTemplatesCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/onboarding/checklist"),
      api.get("/shift-templates").catch(() => ({ data: [] })),
    ]).then(([cl, tpl]) => {
      if (cancelled) return;
      setData(cl.data);
      setShiftTemplatesCount((tpl.data || []).length);
      // Feb 2026 -- the "first-visit redirect" moved to
      // /app/pages/AppIndexRedirect.jsx so it runs BEFORE Overview
      // mounts (no flash). This banner is now purely informational
      // for repeat visits.
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!data) return null;
  const pct = data.percent || 0;
  if (pct >= 100) return null;
  // Feb 2026 -- align customer-facing count with the visible 10-step
  // Account Setup wizard. The `/onboarding/checklist` endpoint
  // returns 13 keys (10 wizard steps + Rota Style + Automation V1 +
  // Publish), which was leaking into the Overview banner as "X of
  // 13". Restrict the counts to the same 10 keys used inside
  // SetupAssistantPage so the two surfaces agree.
  const ACCOUNT_SETUP_KEYS = new Set([
    "company_type", "categories", "skills", "bank_holidays",
    "locations", "team", "pattern", "employee", "first_roster",
    // Shift templates is derived client-side inside the wizard and
    // is not exposed on the checklist endpoint, so we approximate it
    // as complete when both categories and team are done (same rule
    // used by SetupReminderBanner). Small inaccuracy in the middle,
    // self-corrects at 100%.
  ]);
  const steps = data.steps || [];
  const backendDone = steps.filter(
    (s) => ACCOUNT_SETUP_KEYS.has(s.key) && s.done).length;
  // Feb 2026 -- align exactly with the wizard's own derivation:
  // shift templates step is complete when at least one template
  // exists on the tenant (client-side count from /shift-templates).
  const shiftTemplatesDone = (shiftTemplatesCount || 0) > 0;
  const total = 10;
  const done = Math.min(total, backendDone + (shiftTemplatesDone ? 1 : 0));
  const overviewPct = Math.round((done / total) * 100);
  return (
    <Link
      to="/app/setup"
      data-testid="continue-setup-banner"
      className="block mt-4 mb-6 rounded-xl border border-[#E4EAF4] bg-gradient-to-br from-[#F5F8FE] to-[#EFF3FB] px-5 py-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-[#E4EAF4] flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-[#2563EB]" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Continue setup · {overviewPct}%
          </p>
          <p className="text-[14px] font-medium text-[#0B1A36] mt-0.5">
            Finish setting up NextRota ({done} of {total} steps complete)
          </p>
          <div className="h-1.5 bg-[#E4EAF4] rounded-full mt-2 overflow-hidden max-w-[420px]">
            <div className="h-full bg-emerald-500 transition-[width] duration-500"
                 style={{ width: `${overviewPct}%` }} />
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-[#2563EB]" />
      </div>
    </Link>
  );
}

// ---------- R3 (Feb 2026) - Rota Style setup Overview card ----------
//
// Rendered on the Overview page for Workspace Admins once the R1
// backend boolean `account_setup_complete` is true. Copy branches by
// membership using existing plan helpers (`isPaidStarter`,
// `hasGrowthAccess`, `hasCommandAccess`). No new backend state, no
// new endpoint, no new dashboard framework - this is a small existing-
// page card composed from primitives already used elsewhere on this
// file. Zero em / en dashes in the new copy.

function RotaStyleSetupOverviewCard() {
  const [accountSetupComplete, setAccountSetupComplete] = useState(null);
  const [billingMe, setBillingMe] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/onboarding/checklist").then((r) => r.data).catch(() => null),
      api.get("/billing/me").then((r) => r.data).catch(() => null),
    ]).then(([cl, bm]) => {
      if (cancelled) return;
      setAccountSetupComplete(!!cl?.account_setup_complete);
      setBillingMe(bm);
    });
    return () => { cancelled = true; };
  }, []);

  if (accountSetupComplete !== true) return null;
  if (billingMe === null) return null;

  const command = hasCommandAccess(billingMe);
  const growthPlus = hasGrowthAccess(billingMe); // true for growth OR command

  // Starter: locked message + Open Roster
  if (!growthPlus) {
    return (
      <Link
        to="/app/roster"
        data-testid="overview-rota-style-setup-card-starter"
        className="block mt-2 mb-6 rounded-xl border border-[#E4EAF4] bg-white px-5 py-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#F1F5FB] flex items-center justify-center shrink-0">
            <Wand2 className="h-5 w-5 text-[#2563EB]" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Starter plan
            </p>
            <p className="text-[14px] font-semibold text-[#0B1A36] mt-0.5">
              Rota Style setup is available on Growth and above
            </p>
            <p className="text-[12px] text-[#4B5773] mt-1">
              You can still build and publish rosters manually from the Roster page.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 text-[12px] text-[#2563EB] font-medium shrink-0">
            Open Roster <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Link>
    );
  }

  // Growth: single card, opens Rota Style
  if (growthPlus && !command) {
    return (
      <Link
        to="/app/rota-style"
        data-testid="overview-rota-style-setup-card-growth"
        className="block mt-2 mb-6 rounded-xl border border-[#E4EAF4] bg-gradient-to-br from-[#F5F8FE] to-[#EFF3FB] px-5 py-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-[#E4EAF4] flex items-center justify-center shrink-0">
            <Wand2 className="h-5 w-5 text-[#2563EB]" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Next up
            </p>
            <p className="text-[14px] font-semibold text-[#0B1A36] mt-0.5">
              Set up Rota Style
            </p>
            <p className="text-[12px] text-[#4B5773] mt-1">
              Your account setup is complete. Set up advanced planning
              tools to check cover, build reusable patterns and adjust
              rosters faster.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 text-[12px] text-[#2563EB] font-medium shrink-0">
            Open Rota Style <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Link>
    );
  }

  // Command: two cards side by side (Rota Style + Automation).
  return (
    <div className="mt-2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3"
         data-testid="overview-rota-style-setup-cards-command">
      <Link
        to="/app/rota-style"
        data-testid="overview-rota-style-setup-card-command-rota-style"
        className="block rounded-xl border border-[#E4EAF4] bg-gradient-to-br from-[#F5F8FE] to-[#EFF3FB] px-5 py-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-[#E4EAF4] flex items-center justify-center shrink-0">
            <Wand2 className="h-5 w-5 text-[#2563EB]" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Next up
            </p>
            <p className="text-[14px] font-semibold text-[#0B1A36] mt-0.5">
              Set up Rota Style
            </p>
            <p className="text-[12px] text-[#4B5773] mt-1">
              Your account setup is complete. Set up advanced planning
              tools to check cover, build reusable patterns and adjust
              rosters faster.
            </p>
            <span className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#2563EB] font-medium">
              Open Rota Style <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
      <Link
        to="/app/rota-style/automation"
        data-testid="overview-rota-style-setup-card-command-automation"
        className="block rounded-xl border border-[#E4EAF4] bg-white px-5 py-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#DBE7FE] flex items-center justify-center shrink-0">
            <Bot className="h-5 w-5 text-[#1D4ED8]" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Command
            </p>
            <p className="text-[14px] font-semibold text-[#0B1A36] mt-0.5">
              Set up Automation
            </p>
            <p className="text-[12px] text-[#4B5773] mt-1">
              Create draft rosters from Rota Style on a schedule and
              manage automatic publishing where enabled.
            </p>
            <span className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#2563EB] font-medium">
              Open Automation <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}




// ---------- Phase 2Z.m Phase 1 — Permission-aware quick actions ----------
// Admins / team admins / managers see BOTH Rota Style + Roster. Employees
// only see Roster (they can't edit the template). super_admin is handled
// separately by the platform overview path above so we never render here.
// Pure permission-gated routing — no backend call.
function QuickActions({ role }) {
  if (role === "super_admin") return null;
  const adminLike = role === "company_admin"
    || role === "team_admin"
    || role === "manager";
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6"
      data-testid="dashboard-quick-actions"
    >
      {adminLike && (
        <Link
          to="/app/rota-style"
          data-testid="quick-action-rota-style"
          className="group rounded-xl border border-[#E4EAF4] bg-white hover:border-[#5775A6] hover:shadow-sm transition p-4 flex items-start gap-3"
        >
          <span className="rounded-lg bg-[#EEF2FB] text-[#2C407A] p-2">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="flex-1">
            <span className="block text-[10px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Plan the template
            </span>
            <span className="block text-[14px] font-semibold text-[#0B1A36] mt-0.5">
              Open Rota Style
            </span>
            <span className="block text-[11px] text-[#4B5773] mt-0.5">
              Edit the rota template, cycles, and assignment mix.
            </span>
          </span>
          <ArrowRight className="h-4 w-4 text-[#5775A6] opacity-0 group-hover:opacity-100 transition" />
        </Link>
      )}
      <Link
        to={adminLike ? "/app/roster" : "/app/my-roster"}
        data-testid="quick-action-roster"
        className="group rounded-xl border border-[#E4EAF4] bg-white hover:border-[#5775A6] hover:shadow-sm transition p-4 flex items-start gap-3"
      >
        <span className="rounded-lg bg-[#EEF2FB] text-[#2C407A] p-2">
          <CalendarDays className="h-4 w-4" />
        </span>
        <span className="flex-1">
          <span className="block text-[10px] uppercase tracking-widest text-[#5775A6] font-semibold">
            See this week
          </span>
          <span className="block text-[14px] font-semibold text-[#0B1A36] mt-0.5">
            {adminLike ? "Open Roster" : "Open My Roster"}
          </span>
          <span className="block text-[11px] text-[#4B5773] mt-0.5">
            {adminLike
              ? "Review, publish and adjust the live roster."
              : "View your published shifts for the week."}
          </span>
        </span>
        <ArrowRight className="h-4 w-4 text-[#5775A6] opacity-0 group-hover:opacity-100 transition" />
      </Link>
    </div>
  );
}


// ---------- Phase 2Z.m Phase 2 — Overview command-centre cards ----------
// Minimal additive: three premium read-only cards rendered above the
// existing dashboard sections. All data comes from existing endpoints —
// no new backend route, no new collection, no LLM, no fake data.
//
//   1. Rota Mix Intelligence banner — observation + suggestion derived
//      from the F2 minutes-weighted mix on the active Rota Style profile.
//   2. Rota readiness — next Monday's draft/publish state from the
//      pre-flight `/drafts/rota/week-status` endpoint.
//   3. Automation status — Rota Style Automation toggles from
//      `/automation/rota-style/settings`.
//
// All three endpoints are `_company_admin_only`, so the strip only
// renders for `company_admin`. team_admin / manager / employee see
// nothing here — their KPIs and Today panels are unaffected.
function Phase2OverviewCards({ role }) {
  const [rsProfile, setRsProfile] = useState(null); // { profile, cells }
  const [readiness, setReadiness] = useState(null);
  const [automation, setAutomation] = useState(null);
  // Phase 2C - load categories alongside the Rota Style profile so
  // the RMI banner mix chart can identify Away rows and exclude
  // them from the Work denominator. Without this the chart would
  // dilute Work mix by 1/(N+1) on any cell carrying a Break/Away
  // assignment - the same issue we closed for Coverage Guard and
  // RMI in Phase 2A.
  const [awayCategoryIds, setAwayCategoryIds] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (role !== "company_admin") return;
    let cancelled = false;
    Promise.allSettled([
      api.get("/rota-style/profile"),
      api.get("/drafts/rota/week-status"),
      api.get("/automation/rota-style/settings"),
      api.get("/categories"),
    ]).then((results) => {
      if (cancelled) return;
      setRsProfile(results[0].status === "fulfilled" ? results[0].value.data : null);
      setReadiness(results[1].status === "fulfilled" ? results[1].value.data : null);
      setAutomation(results[2].status === "fulfilled" ? results[2].value.data : null);
      if (results[3].status === "fulfilled") {
        const cats = results[3].value.data || [];
        setAwayCategoryIds(new Set(cats
          .filter((c) => c.kind === "away")
          .map((c) => c.category_id)));
      }
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [role]);

  if (role !== "company_admin") return null;
  if (!loaded) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="phase2-overview-cards-loading">
        <Skel /><Skel /><Skel />
      </div>
    );
  }
  return (
    <div className="mb-8 space-y-4" data-testid="phase2-overview-cards">
      <RmiBannerCard rsProfile={rsProfile} awayCategoryIds={awayCategoryIds} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RotaReadinessCard readiness={readiness} />
        <AutomationStatusCard automation={automation} />
      </div>
    </div>
  );
}


// Compute the F2 minutes-weighted mix from the active Rota Style cells.
// Mirrors the rule already used on the Rota Style page so the dashboard
// number matches what managers see when they click through.
// Phase 2C - excludes Away rows from the per-cell denominator. Net
// minutes already subtract the cell-level break, so dividing by
// Work-only count keeps the chart byte-stable on Work-only cells
// and stops Away from diluting Work mix when a cell carries a
// Break/Away assignment.
function _phase2MinutesWeightedMix(cells, awayCategoryIds) {
  const _awaySet = awayCategoryIds instanceof Set ? awayCategoryIds : new Set();
  const _t2m = (t) => {
    if (!t || typeof t !== "string" || !t.includes(":")) return null;
    const [h, m] = t.split(":");
    return Number(h) * 60 + Number(m);
  };
  const minutesByCategory = new Map();
  let totalMinutes = 0;
  for (const c of (cells || [])) {
    const assigns = c.assignments || [];
    if (assigns.length === 0) continue;
    const workAssigns = assigns.filter(
      (a) => !_awaySet.has(a.category_id));
    if (workAssigns.length === 0) continue;
    const s = _t2m(c.start_time);
    const e = _t2m(c.end_time);
    if (s == null || e == null || e <= s) continue;
    const gross = e - s;
    const brk = Math.max(0, Math.min(Number(c.break_minutes) || 0, gross - 1));
    const net = Math.max(0, gross - brk);
    if (net === 0) continue;
    const perAssign = net / workAssigns.length;
    totalMinutes += net;
    for (const a of workAssigns) {
      const name = a.category_name || "(unnamed)";
      minutesByCategory.set(name, (minutesByCategory.get(name) || 0) + perAssign);
    }
  }
  if (totalMinutes === 0) return [];
  return Array.from(minutesByCategory.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, mins]) => ({
      name,
      minutes: Math.round(mins),
      pct: Math.round((mins / totalMinutes) * 100),
    }));
}


function RmiBannerCard({ rsProfile, awayCategoryIds }) {
  const profile = rsProfile?.profile;
  const cells = rsProfile?.cells || [];
  const mix = _phase2MinutesWeightedMix(cells, awayCategoryIds);

  // Empty-state copy is deliberate — never invent a category.
  if (!profile || mix.length === 0) {
    return (
      <RmiHeroShell>
        <div className="flex items-start gap-4">
          <span className="rounded-xl bg-white/10 backdrop-blur ring-1 ring-white/20 p-2.5 shrink-0">
            <Wand2 className="h-5 w-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A8B6E0] font-semibold">
              Rota Mix Intelligence
            </div>
            <div className="text-[18px] sm:text-[20px] font-display text-white mt-1.5 leading-snug"
                 data-testid="rmi-banner-headline">
              {profile ? "No assignments to analyse yet" : "Set up your Rota Style"}
            </div>
            <div className="text-[13px] text-[#C8D2EE] mt-1.5 max-w-xl"
                 data-testid="rmi-banner-suggestion">
              {profile
                ? "Add timed cells with assignments to see your minutes-weighted mix."
                : "Create a Rota Style template to unlock mix insights."}
            </div>
            <span className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-white/90 group-hover:text-white">
              Open Rota Style
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </RmiHeroShell>
    );
  }

  // Observation + suggestion (user choice 2c). Pick the lowest-share
  // category as the suggestion target. If there is only one category,
  // fall back to a balance-neutral message rather than telling the
  // customer to boost the only category they have.
  const lowest = mix[mix.length - 1];
  const highest = mix[0];
  const oneCategoryOnly = mix.length === 1;
  return (
    <RmiHeroShell>
      <div className="flex items-start gap-4 sm:gap-5">
        <span className="rounded-xl bg-white/10 backdrop-blur ring-1 ring-white/20 p-2.5 shrink-0">
          <Wand2 className="h-5 w-5 text-white" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A8B6E0] font-semibold">
              Rota Mix Intelligence
            </div>
            {!oneCategoryOnly && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/40 text-emerald-200 text-[10px] font-semibold px-2 py-0.5"
                data-testid="rmi-banner-boost-pill"
              >
                Boost {lowest.name} +10%
              </span>
            )}
          </div>
          {oneCategoryOnly ? (
            <>
              <div className="text-[18px] sm:text-[20px] font-display text-white mt-1.5 leading-snug"
                   data-testid="rmi-banner-headline">
                {highest.name} is your only assignment ({highest.pct}%)
              </div>
              <div className="text-[13px] text-[#C8D2EE] mt-1.5 max-w-xl"
                   data-testid="rmi-banner-suggestion">
                Add a second category in Rota Style to start blending the mix.
              </div>
            </>
          ) : (
            <>
              <div className="text-[18px] sm:text-[20px] font-display text-white mt-1.5 leading-snug"
                   data-testid="rmi-banner-headline">
                Your lowest share is <span className="text-[#7FB6FF]">{lowest.name}</span> at {lowest.pct}%
              </div>
              <div className="text-[13px] text-[#C8D2EE] mt-1.5 max-w-xl"
                   data-testid="rmi-banner-suggestion">
                Consider boosting {lowest.name} by +10% next cycle to rebalance against {highest.name} ({highest.pct}%).
              </div>
            </>
          )}
          <span className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-white/90 group-hover:text-white">
            Open Rota Style
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </RmiHeroShell>
  );
}


// Shared hero shell — dark navy → indigo gradient with subtle decorative
// SVG accents. Decorative only, NO data charts (per cost-control rules).
// Declared at module scope so React reuses the same component instance
// across re-renders.
function RmiHeroShell({ children }) {
  return (
    <Link
      to="/app/rota-style"
      data-testid="rmi-banner-card"
      className="group block relative overflow-hidden rounded-2xl shadow-[0_8px_30px_rgba(11,26,54,0.18)] hover:shadow-[0_12px_36px_rgba(11,26,54,0.24)] transition-shadow"
      style={{
        background:
          "radial-gradient(120% 140% at 0% 0%, #1E2D5A 0%, #182447 35%, #0F1B36 70%, #0B1530 100%)",
      }}
    >
      {/* Decorative blobs / grid — NOT a data chart. */}
      <div className="pointer-events-none absolute -top-10 -right-10 w-64 h-64 rounded-full opacity-30"
           style={{ background: "radial-gradient(circle, #4F76FF 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-16 -left-12 w-72 h-72 rounded-full opacity-20"
           style={{ background: "radial-gradient(circle, #7C5BFF 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
           style={{
             backgroundImage:
               "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
             backgroundSize: "32px 32px",
           }} />
      <div className="relative p-6 sm:p-7">{children}</div>
    </Link>
  );
}


function RotaReadinessCard({ readiness }) {
  // Derive next week's state without inventing data. If the endpoint
  // returned nothing (offline / error), show a neutral fallback.
  let label = "Not started";
  let tone = "text-[#4B5773]";
  let dot = "bg-slate-300";
  let pillBg = "bg-slate-100 ring-1 ring-slate-200 text-slate-600";
  let iconBg = "bg-slate-50 text-slate-500 ring-1 ring-slate-200";
  let detail = "Generate a draft when you're ready.";

  const ws = readiness?.window_start;
  const we = readiness?.window_end;
  const period = readiness?.existing_period;
  const liveCount = Number(readiness?.live_allocation_count || 0);

  if (period && period.status === "published") {
    label = "Published";
    tone = "text-[#16A34A]";
    dot = "bg-emerald-500";
    pillBg = "bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700";
    iconBg = "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100";
    detail = "Next week is live on the roster.";
  } else if (period && (period.status === "awaiting_review" || period.status === "draft")) {
    label = "Draft ready";
    tone = "text-[#B45309]";
    dot = "bg-amber-500";
    pillBg = "bg-amber-50 ring-1 ring-amber-200 text-amber-700";
    iconBg = "bg-amber-50 text-amber-600 ring-1 ring-amber-100";
    detail = "Review and publish to go live.";
  } else if (!period && liveCount > 0) {
    label = "Live shifts only";
    tone = "text-[#2563EB]";
    dot = "bg-[#2563EB]";
    pillBg = "bg-[#EEF2FB] ring-1 ring-[#C7D4F2] text-[#2C407A]";
    iconBg = "bg-[#EEF2FB] text-[#2C407A] ring-1 ring-[#C7D4F2]";
    detail = `${liveCount} allocation${liveCount === 1 ? "" : "s"} already scheduled.`;
  }

  const dateLine = (ws && we) ? `${ws} → ${we}` : "Next week";

  return (
    <Link
      to="/app/roster"
      data-testid="rota-readiness-card"
      className="group relative rounded-2xl border border-[#E4EAF4] bg-white p-5 hover:border-[#C7D4F2] hover:shadow-[0_8px_24px_rgba(11,26,54,0.08)] transition-all"
    >
      <div className="flex items-start gap-4">
        <span className={`rounded-xl ${iconBg} p-2.5 shrink-0`}>
          <CalendarCheck className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5775A6] font-semibold">
              Rota readiness · next week
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full ${pillBg} text-[10px] font-semibold px-2 py-0.5`}
              data-testid="rota-readiness-pill"
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
              <span className={tone} data-testid="rota-readiness-status">{label}</span>
            </span>
          </div>
          <div className="text-[12px] text-[#4B5773] mt-2" data-testid="rota-readiness-window">
            {dateLine}
          </div>
          <div className="text-[13px] text-[#0B1A36] font-medium mt-1" data-testid="rota-readiness-detail">
            {detail}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-[#5775A6] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
      </div>
    </Link>
  );
}


function AutomationStatusCard({ automation }) {
  const s = automation?.settings || null;
  const enabled = !!(s && s.enabled);
  const autoLeave = !!(s && s.auto_approve_annual_leave_enabled);
  const lastRunAt = automation?.last_run_at || null;

  // Pretty time — UK locale, short.
  let lastRunLabel = "Never";
  if (lastRunAt) {
    try {
      const d = new Date(lastRunAt);
      if (!Number.isNaN(d.getTime())) {
        lastRunLabel = d.toLocaleString("en-GB", {
          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
      }
    } catch { /* keep "Never" */ }
  }

  const pillBg = enabled
    ? "bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700"
    : "bg-slate-100 ring-1 ring-slate-200 text-slate-600";
  const iconBg = enabled
    ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
    : "bg-slate-50 text-slate-500 ring-1 ring-slate-200";

  return (
    <Link
      to="/app/rota-style/automation"
      data-testid="automation-status-card"
      className="group relative rounded-2xl border border-[#E4EAF4] bg-white p-5 hover:border-[#C7D4F2] hover:shadow-[0_8px_24px_rgba(11,26,54,0.08)] transition-all"
    >
      <div className="flex items-start gap-4">
        <span className={`rounded-xl ${iconBg} p-2.5 shrink-0`}>
          <Bot className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5775A6] font-semibold">
              Automation V1
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full ${pillBg} text-[10px] font-semibold px-2 py-0.5`}
              data-testid="automation-status-pill"
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
              {enabled ? "Active" : "Idle"}
            </span>
          </div>
          <div className="text-[13px] font-medium text-[#0B1A36] mt-2" data-testid="automation-status-rsa">
            {enabled ? "Rota Style automation: On" : "Rota Style automation: Off"}
          </div>
          <div className="text-[12px] text-[#4B5773] mt-1" data-testid="automation-status-leave">
            Auto-approve annual leave: <span className="font-medium text-[#0B1A36]">{autoLeave ? "On" : "Off"}</span>
          </div>
          <div className="text-[11px] text-[#5775A6] mt-1.5" data-testid="automation-status-last-run">
            Last run: {lastRunLabel}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-[#5775A6] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
      </div>
    </Link>
  );
}

// ---------- Phase 2Z.m Phase 3 — Fairness & Balance advisory card ----------
// Read-only, advisory only. Surfaces ONE soft balance insight derived
// from existing `/reports/company` data over the last 7 days. NO new
// backend route, NO engine, NO scoring service, NO LLM, NO writes.
// Strictly company_admin (matches the source endpoint's permissions).
//
// Wording is deliberately soft — see _phase3HarshTerms below for the
// banned vocabulary list that's also enforced by a source-level test.
//
// Insight pickers (returns null when there isn't enough data):
//   • "less exposure"  — an employee whose work-category minutes are
//     ≤ 50% of the team mean for that category over the period.
//   • "concentrated"   — one employee holds > 60% of the team total
//     for a single work category over the period.
//
// Safeguards (all enforced by the picker):
//   • ≥ 3 employees with any allocated work minutes in the period
//   • ≥ 2 distinct work categories present
//   • the chosen category must have ≥ 4 hours (240 min) of team total
//   • only ONE insight is ever shown
function FairnessBalanceCard({ role }) {
  const [report, setReport] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (role !== "company_admin") return;
    let cancelled = false;
    const to = new Date();
    const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fmt = (d) => d.toISOString().slice(0, 10);
    api.get("/reports/company", {
      params: { from_date: fmt(from), to_date: fmt(to) },
    }).then((r) => {
      if (cancelled) return;
      setReport(r.data || null);
      setLoaded(true);
    }).catch(() => {
      if (cancelled) return;
      setReport(null);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [role]);

  if (role !== "company_admin") return null;
  if (!loaded) {
    return (
      <div className="mb-8" data-testid="fairness-balance-card-loading">
        <Skel />
      </div>
    );
  }

  const insight = _phase3PickInsight(report);

  return (
    <div className="mb-8" data-testid="fairness-balance-card">
      <div className="relative overflow-hidden rounded-2xl border border-[#E4EAF4] bg-white shadow-[0_4px_14px_rgba(11,26,54,0.05)]">
        {/* Soft amber accent stripe on the left — premium advisory feel. */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-400 to-amber-300" />
        <div className="flex flex-col sm:flex-row items-stretch gap-0">
          {/* Icon column. */}
          <div className="flex items-center justify-center sm:w-24 px-5 pt-5 sm:pt-6 sm:pb-6">
            <span className="rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 ring-1 ring-amber-200 p-3 shrink-0">
              <Scale className="h-6 w-6 text-amber-600" />
            </span>
          </div>
          {/* Content column. */}
          <div className="flex-1 min-w-0 px-5 sm:pl-2 sm:pr-6 py-5 sm:py-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5775A6] font-semibold">
                Fairness &amp; Balance
              </div>
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-700 text-[10px] font-semibold px-2 py-0.5"
                data-testid="fairness-balance-pill"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                Advisory
              </span>
            </div>
            {!insight ? (
              <div className="text-[13px] text-[#4B5773] mt-2"
                   data-testid="fairness-balance-empty">
                Not enough rota history yet for a balance insight.
              </div>
            ) : (
              <>
                <div className="text-[15px] sm:text-[16px] font-display text-[#0B1A36] mt-2 leading-snug"
                     data-testid="fairness-balance-headline">
                  {insight.headline}
                </div>
                <div className="text-[13px] text-[#4B5773] mt-2 max-w-2xl"
                     data-testid="fairness-balance-suggestion">
                  {insight.suggestion}
                </div>
                <Link
                  to="/app/rota-style"
                  data-testid="fairness-balance-link"
                  className="inline-flex items-center gap-1.5 mt-3 text-[12px] font-semibold text-[#2563EB] bg-[#EEF2FB] ring-1 ring-[#C7D4F2] rounded-full px-3 py-1 hover:bg-[#E2E9FB] transition-colors"
                >
                  Open Rota Style <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </>
            )}
            <div className="text-[11px] text-[#5775A6] mt-3 italic"
                 data-testid="fairness-balance-disclaimer">
              Advisory only. No automatic changes. Consider balancing if skills and coverage allow, to support fair workload distribution.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Banned vocabulary is locked by `test_phase_2zm_overview_phase3.py`
// (search for `banned = (...)`). Don't introduce harsh / judgemental
// language inside this card — the source-level test will fail.
//
// Pure, deterministic picker. Returns null when the safeguards aren't
// met. NEVER invents categories or employee names — every value is
// pulled straight from the existing report payload.
function _phase3PickInsight(report) {
  if (!report) return null;
  const employees = report.employees || [];
  const categories = report.categories || [];
  if (!employees.length || !categories.length) return null;

  // Build a category index restricted to WORK kind, with name preserved.
  const workCats = categories.filter((c) => c.kind === "work");
  if (workCats.length < 2) return null;
  const catNameById = new Map(workCats.map((c) => [c.category_id, c.name]));

  // Employees with any allocated work minutes (the "active" sample).
  const active = employees.filter((e) => (Number(e.work_minutes) || 0) > 0);
  if (active.length < 3) return null;

  // For each work category, compute per-employee minutes (across the
  // active sample), the team total and the team mean.
  let best = null; // largest gap_ratio insight found so far
  for (const cat of workCats) {
    const totals = active.map((e) => ({
      employee_id: e.employee_id,
      name: e.name,
      minutes: Number((e.by_category || {})[cat.category_id] || 0),
    }));
    const teamTotal = totals.reduce((s, x) => s + x.minutes, 0);
    if (teamTotal < 240) continue; // ≥ 4h of team total required
    const mean = teamTotal / active.length;
    if (mean <= 0) continue;

    // --- "concentrated" detection: one employee > 60% of the team total
    const sortedDesc = [...totals].sort((a, b) => b.minutes - a.minutes);
    const top = sortedDesc[0];
    const topShare = top.minutes / teamTotal;
    if (topShare > 0.60 && active.length >= 3) {
      const insight = {
        kind: "concentrated",
        gap_ratio: topShare,
        category_name: cat.name,
        employee_name: top.name,
        headline: `${cat.name} work is currently concentrated with ${top.name} over the last 7 days.`,
        suggestion: `Consider spreading ${cat.name} across the team if suitable, to support fair workload distribution.`,
      };
      if (!best || insight.gap_ratio > best.gap_ratio) best = insight;
    }

    // --- "less exposure" detection: an employee ≤ 50% of team mean
    const sortedAsc = [...totals].sort((a, b) => a.minutes - b.minutes);
    const low = sortedAsc[0];
    if (low.minutes <= 0.5 * mean) {
      const gap = (mean - low.minutes) / mean; // 0..1
      const insight = {
        kind: "less_exposure",
        gap_ratio: gap,
        category_name: cat.name,
        employee_name: low.name,
        headline: `${low.name} has had less exposure to ${cat.name} than the team average over the last 7 days.`,
        suggestion: `Consider one additional ${cat.name} block for ${low.name} next week if skills and coverage allow.`,
      };
      if (!best || insight.gap_ratio > best.gap_ratio) best = insight;
    }
  }

  return best;
}

void _phase3PickInsight; // referenced by source-level test only

